import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '../lib/api';
import { approvalsApi, type Approval, type DiscountAuthority } from '../lib/approvalsApi';
import { useAuth } from '../context/AuthContext';

const rupees = (p: number) =>
  '₹' + (p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ROLE_LABELS: Record<string, string> = {
  TrustAdmin: 'Trust admin',
  CampusAdmin: 'Campus admin',
  Accountant: 'Accountant',
  Teacher: 'Teacher',
};

/**
 * The Approval Engine surface.
 *  - Management (TrustAdmin / CampusAdmin) sees the Inbox and the Discount-cap config.
 *  - Finance (Accountant) sees only their own raised requests.
 * The backend enforces this too; the tabs just mirror it.
 */
export function ApprovalsPage() {
  const { user } = useAuth();
  const isManager = user?.role === 'TrustAdmin' || user?.role === 'CampusAdmin';
  const [tab, setTab] = useState<'inbox' | 'mine' | 'caps'>(isManager ? 'inbox' : 'mine');

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Approvals</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Approvals</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: 640 }}>
          Discounts requested in someone's name route here. Within a staff member's approved cap they
          apply straight away; above it, they wait for a manager to approve or reject.
        </p>
      </div>

      <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
        {isManager && <TabBtn active={tab === 'inbox'} onClick={() => setTab('inbox')}>Inbox</TabBtn>}
        <TabBtn active={tab === 'mine'} onClick={() => setTab('mine')}>My requests</TabBtn>
        {isManager && <TabBtn active={tab === 'caps'} onClick={() => setTab('caps')}>Discount caps</TabBtn>}
      </div>

      {tab === 'inbox' && isManager && <InboxTab />}
      {tab === 'mine' && <MineTab />}
      {tab === 'caps' && isManager && <CapsTab />}
    </div>
  );
}

function InboxTab() {
  const [items, setItems] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await approvalsApi.list({ status: 'Pending', take: 100 });
      setItems(res.items);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const decide = async (id: string, decision: 'approve' | 'reject') => {
    const note = decision === 'reject'
      ? (window.prompt('Reason for rejecting (optional):') ?? undefined)
      : undefined;
    setBusyId(id); setMsg(null);
    try {
      await approvalsApi.decide(id, decision, note);
      setMsg(decision === 'approve' ? 'Approved — the discount has been applied.' : 'Rejected.');
      await load();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not record the decision.');
    } finally { setBusyId(null); }
  };

  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (items.length === 0) return <Empty text="Nothing waiting for approval. When a discount exceeds a staff member's cap, it appears here." />;

  return (
    <div>
      {msg && <Banner text={msg} />}
      <div style={{ display: 'grid', gap: 12 }}>
        {items.map((a) => (
          <div key={a.id} className="card" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 600 }}>{a.subjectLabel}</div>
                <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
                  Discount of <strong>{rupees(a.amountPaise)}</strong>
                  {a.requestedInName && <> · requested in the name of <strong>{a.requestedInName}</strong></>}
                </div>
                {a.reason && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>Reason: {a.reason}</div>}
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 6 }}>
                  Raised {new Date(a.requestedAt).toLocaleString('en-IN')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <button
                  className="btn btn-primary"
                  disabled={busyId === a.id}
                  onClick={() => decide(a.id, 'approve')}
                >Approve</button>
                <button
                  className="btn"
                  disabled={busyId === a.id}
                  onClick={() => decide(a.id, 'reject')}
                >Reject</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MineTab() {
  const [items, setItems] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await approvalsApi.mine()); }
    catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const withdraw = async (id: string) => {
    setBusyId(id);
    try { await approvalsApi.withdraw(id); await load(); }
    catch { /* ignore */ }
    finally { setBusyId(null); }
  };

  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (items.length === 0) return <Empty text="You haven't raised any approvals yet. Raise a fee discount from a student's invoice." />;

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {items.map((a) => (
        <div key={a.id} className="card" style={{ padding: 14, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 600 }}>{a.subjectLabel}</div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
              {rupees(a.amountPaise)}{a.requestedInName && <> · in the name of {a.requestedInName}</>}
            </div>
            {a.status === 'Approved' && a.decisionSource === 'AutoWithinCap' &&
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>Auto-approved within your cap.</div>}
            {a.decisionNote && <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>Note: {a.decisionNote}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <StatusPill status={a.status} />
            {a.status === 'Pending' && (
              <button className="btn" disabled={busyId === a.id} onClick={() => withdraw(a.id)}>Withdraw</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function CapsTab() {
  const [rows, setRows] = useState<DiscountAuthority[]>([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // the roles a school actually delegates discounts to
  const editableRoles = ['Accountant', 'CampusAdmin', 'Teacher'];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const existing = await approvalsApi.authorities();
      const byRole = new Map(existing.map((r) => [r.role, r]));
      setRows(editableRoles.map((role) =>
        byRole.get(role) ?? { role, maxSelfApproveBps: 0, maxSelfApprovePaise: 0 }));
    } catch { setRows([]); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async (row: DiscountAuthority) => {
    setBusy(true); setMsg(null);
    try {
      await approvalsApi.setAuthority(row);
      setMsg(`Saved cap for ${ROLE_LABELS[row.role] ?? row.role}.`);
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;

  return (
    <div className="card" style={{ padding: 20, maxWidth: 640 }}>
      <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 16 }}>
        How much of a discount each role can grant on their own. Anything above the cap needs a
        manager's approval. A cap of 0% means that role must always send discounts for approval.
      </p>
      {msg && <Banner text={msg} />}
      <div style={{ display: 'grid', gap: 14 }}>
        {rows.map((row, i) => (
          <div key={row.role} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 130, fontWeight: 600 }}>{ROLE_LABELS[row.role] ?? row.role}</div>
            <label style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              Cap %
              <input
                type="number" min={0} max={100} step={0.5}
                value={row.maxSelfApproveBps / 100}
                onChange={(e) => {
                  const pct = parseFloat(e.target.value || '0');
                  const next = [...rows];
                  next[i] = { ...row, maxSelfApproveBps: Math.round(pct * 100) };
                  setRows(next);
                }}
                style={{ width: 80, marginLeft: 8 }}
              />
            </label>
            <label style={{ fontSize: 12, color: 'var(--ink-2)' }}>
              Max ₹ (0 = none)
              <input
                type="number" min={0} step={100}
                value={row.maxSelfApprovePaise / 100}
                onChange={(e) => {
                  const rup = parseFloat(e.target.value || '0');
                  const next = [...rows];
                  next[i] = { ...row, maxSelfApprovePaise: Math.round(rup * 100) };
                  setRows(next);
                }}
                style={{ width: 110, marginLeft: 8 }}
              />
            </label>
            <button className="btn btn-primary" disabled={busy} onClick={() => save(row)}>Save</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- small shared bits ----

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer',
        fontWeight: active ? 600 : 400, color: active ? 'var(--ink)' : 'var(--ink-3)',
        borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent', marginBottom: -1,
      }}
    >{children}</button>
  );
}

function StatusPill({ status }: { status: string }) {
  const color =
    status === 'Approved' ? 'var(--paid, #2e7d32)'
    : status === 'Rejected' ? '#c62828'
    : status === 'Withdrawn' ? 'var(--ink-3)'
    : 'var(--pending, #b8860b)';
  return (
    <span style={{ fontSize: 12, fontWeight: 600, color, whiteSpace: 'nowrap' }}>{status}</span>
  );
}

function Banner({ text }: { text: string }) {
  return <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 12 }}>{text}</div>;
}

function Empty({ text }: { text: string }) {
  return <p style={{ color: 'var(--ink-3)', fontSize: 14, maxWidth: 520 }}>{text}</p>;
}
